import { css } from '@emotion/react';
import {
  Box,
  Button,
  Flex,
  Icon,
  Pagination,
  Stack,
  mobileFirstMediaQuery,
  openConfirmModal,
  useShowNotification,
} from '@inflearn/ds-react';
import { useLang } from '@inflearn/i18n-tools';
import {
  CertificatePreviewModal,
  CourseFolderModal,
  type CourseReviewFormType,
  CreateCourseReviewModal,
  EditCourseReviewModal,
  RealNameModal,
} from '@inflearn/modals';
import { MonitoringService } from '@inflearn/monitoring';
import { faArrowLeft, faArrowRight, faBoxArchive } from '@inflearn/pro-regular-svg-icons';
import { useDisclosure } from '@mantine/hooks';
import { useQueryClient } from '@tanstack/react-query';
import { defaults, find, isNil, map } from 'lodash-es';
import { useTranslation } from 'next-i18next';
import Link from 'next/link';
import { useRouter } from 'next/router';
import qs from 'query-string';
import { type ReactNode, createContext, useContext, useState } from 'react';

import {
  CourseType,
  type OffsetPageResponseMyVoucherDto
} from '../../../../../resources/@types/courseSchema';
import { ApiError } from '../../../../../resources/ApiError';
import { logger } from '../../../../../resources/Logger';
import { MY_URL } from '../../../../constants/urls';
import { useCertificate } from '../../../../hooks/useCertificate';
import { useCopyMarketingPartnerLink } from '../../../../hooks/useCopyMarketingPartnerLink';
import useClientAccountProfileQuery, {
  getAccountProfileQueryKey,
} from '../../../../queries/useClientAccountProfileQuery';
import { useClientApiCourseCertificatesDetailQuery } from '../../../../queries/useClientApiCourseCertificatesDetailQuery';
import { useCourseCertificatesChallengeDetailQuery } from '../../../../queries/useCourseCertificatesChallengeDetailQuery';
import { eventNames } from '../../../../utils/GTM/eventNames';
import { useTrackMixpanelEvent } from '../../../../utils/GTM/useTrackMixpanelEvent';
import { DeferredComponent } from '../../../util/DeferredComponent';
import CourseCard from './CourseCard/CourseCard';
import {
  type CourseCardData,
  courseCardDataTransformer,
} from './CourseCard/courseCardDataTransformer';
import { CourseCardContainerSkeleton } from './CourseCardContainerSkeleton';
import { useClientApiVoucherArchiveUpdateMutation } from './mutations/useClientApiVoucherArchiveUpdateMutation';
import { useClientApiVouchersIdFreeDeleteMutation } from './mutations/useClientApiVouchersIdFreeDeleteMutation';
import { useClientApiVouchersIdFreeDeleteMutationForB2B } from './mutations/useClientApiVouchersIdFreeDeleteMutationForB2B';
import { useClientApiVouchersMyQueryRootResetKey } from './queries/useClientApiVoucherMyQuery';

export function CourseCardContainer({
  data,
  isLoading,
  isError,
  isArchivedPage,
  emptyState,
  errorState,
  changePage,
  courseType,
}: {
  isLoading: boolean;
  isError: boolean;
  isArchivedPage?: boolean;
  data?: OffsetPageResponseMyVoucherDto;
  emptyState?: ReactNode;
  errorState?: ReactNode;
  changePage: (value: number) => void;
  courseType: CourseType;
}) {
  const { t } = useTranslation();
  const { lang } = useLang();

  const router = useRouter();
  const queryClient = useQueryClient();

  const { data: profile } = useClientAccountProfileQuery();
  const userId = profile?.id;

  const { trackMixpanelEvent } = useTrackMixpanelEvent();

  const invalidateCourses = () => {
    queryClient.invalidateQueries(useClientApiVouchersMyQueryRootResetKey);
  };

  const [isCourseReviewModalOpened, reviewModalHandlers] = useDisclosure(false);
  const [reviewTarget, setReviewTarget] = useState<{
    courseId: number;
    reviewId?: number;
    review?: CourseCardData['review'];
  } | null>(null);

  const [isFolderingModalOpened, folderModalHandlers] = useDisclosure(false);
  const [folderingTargetId, setFolderingTargetId] = useState<number | null>(null);
  const { showNotification } = useShowNotification();
  const hideVoucherMutation = useClientApiVoucherArchiveUpdateMutation({
    onSuccess: invalidateCourses,
    onError: (error: unknown) => {
      logger.error(error);

      if (ApiError.isApiError(error)) {
        showNotification({
          title: error.message,
          type: 'error',
        });
      }
    },
  });

  const [cancelTargetVoucherId, setCancelTargetVoucherId] = useState<number>(0);

  const cancelVoucherMutation = useClientApiVouchersIdFreeDeleteMutation(
    {
      voucherId: cancelTargetVoucherId,
    },
    {
      onSuccess: invalidateCourses,
      onError: (error: unknown) => {
        logger.error(error);

        if (ApiError.isApiError(error)) {
          showNotification({
            title: error.message,
            type: 'error',
          });
        }
      },
    },
  );

  const cancelVoucherMutationForB2B = useClientApiVouchersIdFreeDeleteMutationForB2B(
    {
      voucherId: cancelTargetVoucherId,
    },
    {
      onSuccess: invalidateCourses,
      onError: (error: unknown) => {
        logger.error(error);

        if (ApiError.isApiError(error)) {
          showNotification({
            title: error.message,
            type: 'error',
          });
        }
      },
    },
  );

  const { copyMarketingPartnerLink } = useCopyMarketingPartnerLink();

  const copyPromotionLink = async (voucherId: number) => {
    const voucher = find(data?.items, { id: voucherId });

    if (!voucher) {
      MonitoringService.getInstance().datadog.addError(
        `voucherId: ${voucherId}: 바우처 정보를 찾을 수 없습니다.`,
        {
          voucherId,
          userId: profile?.id,
        },
      );

      return;
    }

    trackMixpanelEvent(eventNames.CLICK_PARTNERS_SHARE_LINK_ON_MYCOURSES, {
      course_identification: voucher.course.id.toString(),
      course_title: voucher.course.title,
      seq0_instructor_id: voucher.course.instructorId.toString(),
      seq0_instructor_name: voucher.course.instructorName,
      course_limit_at: voucher.limitedAt || t('무제한'),
      progress: Number(voucher.progress),
    });

    copyMarketingPartnerLink(voucher.course.id);
  };

  /**
   * reviewId가 있으면 수정 모달을 띄우고, 없으면 작성 모달을 띄운다.
   */
  const openReviewModal = (voucherId: number) => {
    const voucher = find(data?.items, { id: voucherId });

    if (!voucher) {
      MonitoringService.getInstance().datadog.addError(
        `voucherId: ${voucherId}: 바우처 정보를 찾을 수 없습니다.`,
      );

      return;
    }

    setReviewTarget({
      courseId: voucher.course.id,
      reviewId: voucher.review?.id,
      review: voucher.review,
    });
    trackMixpanelEvent(eventNames.VIEW_REVIEW_MODAL, {
      title: voucher.course.title,
      progress: Number(voucher.progress),
      course_limited_at: voucher.limitedAt || t('무제한'),
      course_identification: voucher.course.id,
      click_spot: 'mycourses',
      my_star_rate: voucher.review?.rating ? voucher.review?.rating : null,
    });

    reviewModalHandlers.open();
  };

  const openFolderModal = (courseId: number) => {
    setFolderingTargetId(courseId);

    folderModalHandlers.open();
  };

  const confirmHide = (fn: () => void) => {
    openConfirmModal({
      title: t('강의 숨기기'),
      onConfirm: () => {
        fn();
      },
      labels: {
        confirm: t('숨기기'),
        cancel: t('다음에'),
      },
      children: (
        <>
          {t('이 강의를 숨김 처리할까요?')}
          <br />
          {t('숨긴 강의는 [숨긴 강의 보기]에서 확인할 수 있습니다.')}
        </>
      ),

      footerType: 'right',
      size: 'sm',
    });
  };

  const hideCourse = (voucherId: number) => {
    confirmHide(() => {
      hideVoucherMutation.mutate(
        {
          id: voucherId,
          isArchive: true,
        },
        {
          onSuccess: () => {
            showNotification({
              title: t('강의를 목록에서 숨겼어요.'),
              type: 'success',
            });
          },
        },
      );
    });
  };

  const confirmCancel = (fn: () => void) => {
    openConfirmModal({
      title: t('수강 취소'),
      onConfirm: () => {
        fn();
      },
      labels: {
        confirm: t('수강 취소'),
        cancel: t('다음에'),
      },
      children: (
        <>
          {t('이 강의를 수강 취소할까요?')}

          <br />
          {t('무료 강의는 즉시 취소가 가능합니다.')}
        </>
      ),

      footerType: 'right',
      size: 'sm',
      confirmProps: {
        color: 'red',
      },
    });
  };

  const cancelCourse = (voucherId: number) => {
    setCancelTargetVoucherId(voucherId);

    const targetVoucher = find(data?.items, (item) => item.id === voucherId);

    if (!targetVoucher) {
      MonitoringService.getInstance().datadog.addError(
        `voucherId: ${voucherId}: 바우처 정보를 찾을 수 없습니다.`,
      );

      return;
    }
    confirmCancel(() => {
      if (targetVoucher.cancelType === 'B2B') {
        cancelVoucherMutationForB2B.mutate({
          onSuccess: () => {
            showNotification({
              title: t('강의 수강을 취소했어요.'),
              type: 'success',
            });
          },
        });

        return;
      }

      cancelVoucherMutation.mutate({
        onSuccess: () => {
          showNotification({
            title: t('강의 수강을 취소했어요.'),
            type: 'success',
          });
        },
      });
    });
  };

  const revealCourse = (voucherId: number) => {
    hideVoucherMutation.mutate({
      id: voucherId,
      isArchive: false,
    });
  };

  const handleReviewSubmitSuccess = (reviewData: CourseReviewFormType) => {
    reviewModalHandlers.close();
    setReviewTarget(null);
    invalidateCourses();

    const targetVoucher = find(data?.items, (item) => item.course.id === reviewTarget?.courseId);

    if (!targetVoucher) {
      return;
    }

    trackMixpanelEvent(eventNames.SUBMIT_REVIEW_MY_COURSES, {
      my_star_rate: reviewData.rating,
      unit_review_tag: reviewData.reasons,
      review_body: reviewData.body,
      progress: Number(targetVoucher.progress),
      course_identification: targetVoucher.course.id,
      star_rate: isNil(targetVoucher.review) ? null : targetVoucher.review.rating,
      course_type:
        courseType === CourseType.ON ? 'on' : courseType === CourseType.OFF ? 'off' : 'challenge',
    });
  };

  const handleReviewModalClose = () => {
    reviewModalHandlers.close();
    setReviewTarget(null);
  };

  const handleFolderingSubmitSuccess = () => {
    folderModalHandlers.close();
    setFolderingTargetId(null);
  };

  const [certificateTarget, setCertificateTarget] = useState<{
    courseId: number;
    voucherId: number;
    courseType: CourseType;
  } | null>(null);

  const {
    isRealNameModalOpened,
    isCertificatedModalOpened,
    closeCertificate,
    onClickCertificateModalOpen,
    closeRealNameModal,
  } = useCertificate();

  const { data: onlineCertificateDetail } = useClientApiCourseCertificatesDetailQuery({
    query: {
      userId: userId || 0,
      courseId: certificateTarget?.courseId || 0,
      voucherId: certificateTarget?.voucherId || 0,
    },
    options: {
      enabled: !!(
        certificateTarget &&
        userId &&
        certificateTarget.courseType === CourseType.ON &&
        isCertificatedModalOpened
      ),
    },
  });

  const { data: challengeCertificateDetail } = useCourseCertificatesChallengeDetailQuery({
    query: {
      userId: userId || 0,
      courseId: certificateTarget?.courseId || 0,
      voucherId: certificateTarget?.voucherId || 0,
    },
    options: {
      enabled: !!(
        certificateTarget &&
        userId &&
        certificateTarget.courseType === CourseType.CHALLENGE &&
        isCertificatedModalOpened
      ),
    },
  });

  const openCertificateModal = (voucherId: number) => {
    const voucher = find(data?.items, { id: voucherId });

    if (!voucher) {
      MonitoringService.getInstance().datadog.addError(
        `voucherId: ${voucherId}: 바우처 정보를 찾을 수 없습니다.`,
      );

      return;
    }

    setCertificateTarget({
      courseId: voucher.course.id,
      voucherId: voucherId,
      courseType,
    });

    onClickCertificateModalOpen({
      userId,
      courseId: voucher.course.id,
      voucherId,
    });
  };

  if (isLoading) {
    return (
      <DeferredComponent>
        <CourseCardContainerSkeleton />
      </DeferredComponent>
    );
  }

  const courseCards = map(data?.items, courseCardDataTransformer);
  const isEmpty = !courseCards || !courseCards.length;
  const { totalPage, pageNumber } = defaults(data, {
    totalPage: 0,
    pageNumber: 0,
  });

  function HideHiddenCourseButton() {
    return (
      <Button
        component={Link}
        variant="default"
        color="infgreen"
        size="sm"
        radius="md"
        href={qs.stringifyUrl({
          url: MY_URL.COURSES(),
          query: {
            ...router.query,
            isArchived: 'false',
          },
        })}
        leftIcon={<Icon icon={faArrowLeft} size="md" />}
      >
        {t('돌아가기')}
      </Button>
    );
  }

  function ShowHiddenCourseButton() {
    return (
      <Button
        component={Link}
        variant="default"
        color="infgreen"
        size="sm"
        radius="md"
        href={qs.stringifyUrl({
          url: MY_URL.ARCHIVED_COURSES(),
          query: {
            ...router.query,
            isArchived: 'true',
          },
        })}
        leftIcon={<Icon icon={faBoxArchive} />}
        rightIcon={<Icon icon={faArrowRight} size="md" />}
      >
        {t('숨긴 강의 보기')}
      </Button>
    );
  }

  return (
    <Stack spacing="md">
      {isRealNameModalOpened && (
        <RealNameModal
          lang={lang}
          type="enrollment"
          opened={isRealNameModalOpened}
          onClose={closeRealNameModal}
          invalidateQueryKey={getAccountProfileQueryKey()}
        />
      )}

      {isCertificatedModalOpened && userId && certificateTarget && (
        <>
          {certificateTarget.courseType === CourseType.ON && onlineCertificateDetail && (
            <CertificatePreviewModal
              courseType={certificateTarget.courseType}
              lang={lang}
              userId={userId}
              courseId={certificateTarget.courseId}
              voucherId={certificateTarget.voucherId}
              isOpened={isCertificatedModalOpened}
              onClose={closeCertificate}
              certificateDetail={{
                ...onlineCertificateDetail,
                courseTitle: onlineCertificateDetail.title,
              }}
              onClickDownloadButton={() =>
                trackMixpanelEvent(eventNames.CLICK_SHARE_CERTIFICATE, {
                  type: 'download',
                })
              }
              onClickLinkCopyButton={() =>
                trackMixpanelEvent(eventNames.CLICK_SHARE_CERTIFICATE, {
                  type: 'linkcopy',
                })
              }
              onClickLinkedInButton={() =>
                trackMixpanelEvent(eventNames.CLICK_SHARE_CERTIFICATE, {
                  type: 'linkedin',
                })
              }
            />
          )}
          {certificateTarget.courseType === CourseType.CHALLENGE && challengeCertificateDetail && (
            <CertificatePreviewModal
              courseType={certificateTarget.courseType}
              lang={lang}
              userId={userId}
              courseId={certificateTarget.courseId}
              voucherId={certificateTarget.voucherId}
              isOpened={isCertificatedModalOpened}
              onClose={closeCertificate}
              certificateDetail={{
                ...challengeCertificateDetail,
                courseTitle: challengeCertificateDetail.title,
              }}
              onClickDownloadButton={() =>
                trackMixpanelEvent(eventNames.CLICK_SHARE_CERTIFICATE, {
                  type: 'download',
                })
              }
              onClickLinkCopyButton={() =>
                trackMixpanelEvent(eventNames.CLICK_SHARE_CERTIFICATE, {
                  type: 'linkcopy',
                })
              }
              onClickLinkedInButton={() =>
                trackMixpanelEvent(eventNames.CLICK_SHARE_CERTIFICATE, {
                  type: 'linkedin',
                })
              }
            />
          )}
        </>
      )}
      {folderingTargetId && (
        <CourseFolderModal
          lang={lang}
          onClose={handleFolderingSubmitSuccess}
          opened={isFolderingModalOpened}
          courseId={folderingTargetId}
        />
      )}
      {reviewTarget && reviewTarget.reviewId === undefined && (
        <CreateCourseReviewModal
          lang={lang}
          courseId={reviewTarget.courseId}
          isOpened={isCourseReviewModalOpened}
          onClose={handleReviewModalClose}
          handleSuccess={handleReviewSubmitSuccess}
        />
      )}
      {!!reviewTarget?.review && (
        <EditCourseReviewModal
          lang={lang}
          isOpened={isCourseReviewModalOpened}
          onClose={handleReviewModalClose}
          reviewId={reviewTarget.review.id}
          initialValue={{
            body: reviewTarget.review.content,
            rating: reviewTarget.review.rating,
            reasons: reviewTarget.review.reasons as CourseReviewFormType['reasons'],
          }}
          handleSuccess={handleReviewSubmitSuccess}
        />
      )}

      {!isEmpty && (
        <CourseReviewContext.Provider
          value={{
            openReviewModal,
            openFolderModal,
            openCertificateModal,
            hideCourse,
            revealCourse,
            cancelCourse,
            copyPromotionLink,
          }}
        >
          <Box css={styleListContainer}>
            {courseCards.map((course) => (
              <CourseCard {...course} courseType={courseType} key={course.id} />
            ))}
          </Box>
        </CourseReviewContext.Provider>
      )}
      {isEmpty && (isError ? errorState : emptyState)}
      <Flex justify={isArchivedPage ? 'flex-start' : 'flex-end'} my={16}>
        {isArchivedPage ? <HideHiddenCourseButton /> : <ShowHiddenCourseButton />}
      </Flex>
      {!isEmpty && totalPage > 1 && (
        <Flex justify="center">
          <Pagination
            onChange={changePage}
            withControls
            size="md"
            value={pageNumber}
            total={totalPage}
          />
        </Flex>
      )}
    </Stack>
  );
}

export const CourseReviewContext = createContext<{
  openReviewModal: (voucherId: number) => void;
  openCertificateModal: (voucherId: number) => void;
  openFolderModal: (courseId: number) => void;
  hideCourse: (voucherId: number) => void;
  revealCourse: (voucherId: number) => void;
  cancelCourse: (voucherId: number) => void;
  copyPromotionLink: (voucherId: number) => void;
}>({
  openReviewModal: () => null,
  openCertificateModal: () => null,
  openFolderModal: () => null,
  hideCourse: () => null,
  revealCourse: () => null,
  cancelCourse: () => null,
  copyPromotionLink: () => null,
});

export const useCourseReviewContext = () => {
  const context = useContext(CourseReviewContext);

  if (!context) {
    throw new Error('useCourseReviewContext must be used within CourseReviewContext');
  }

  return context;
};

export const styleListContainer = css({
  display: 'grid',
  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  rowGap: 48,
  columnGap: 16,
  [mobileFirstMediaQuery.tablet]: {
    // minmax는 Ellipsis를 위한것
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
  },
});
